import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, ArrowLeft, RefreshCw } from 'react-feather';
import logo from '../../../assets/logo.png';
import GradientButton from '../common/GradientButton';
import { useTheme } from 'styled-components';
import { LegalFooter } from '../auth/LoginScreen.styles';

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: ${p => p.theme.colors.background.paper};
    align-items: center;
    justify-content: center;
    padding: 24px 20px;
    position: relative;
`;

const BackButton = styled.button`
    position: absolute;
    top: 24px;
    left: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: ${p => p.theme.colors.text.secondary};
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 8px;
    transition: all 0.2s;
    &:hover {
        background-color: ${p => p.theme.colors.background.main};
        color: ${p => p.theme.colors.text.primary};
    }
`;

const LogoImg = styled.img`
    width: 130px;
    height: auto;
    object-fit: contain;
    margin-bottom: 32px;
    filter: ${p => p.theme.isDark ? 'brightness(0) invert(1)' : 'brightness(0)'};
`;

const Card = styled.div`
    width: 100%;
    max-width: 420px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const IconCircle = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: ${p => p.theme.colors.background.selected};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    color: ${p => p.theme.colors.text.primary};
`;

const Title = styled.h1`
    font-family: 'Inter', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: ${p => p.theme.colors.text.primary};
    margin: 0 0 8px 0;
    letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: ${p => p.theme.colors.text.secondary};
    margin: 0 0 32px 0;
    line-height: 1.6;
`;

const EmailBadge = styled.span`
    font-weight: 600;
    color: ${p => p.theme.colors.text.primary};
`;

const OtpRow = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 32px;
    justify-content: center;
`;

const OtpInput = styled.input`
    width: 50px;
    height: 56px;
    text-align: center;
    font-family: 'Inter', sans-serif;
    font-size: 22px;
    font-weight: 700;
    border: 2px solid ${({ $filled, $error, theme }) =>
        $error ? '#ef4444' : $filled ? theme.colors.text.primary : theme.colors.border.main};
    border-radius: 12px;
    background-color: ${p => p.theme.colors.background.paper};
    color: ${p => p.theme.colors.text.primary};
    outline: none;
    transition: all 0.15s;
    caret-color: transparent;
    &:focus {
        border-color: ${p => p.theme.colors.text.primary};
        box-shadow: 0 0 0 3px ${p => p.theme.colors.background.selected};
    }
    @media (max-width: 400px) {
        width: 42px;
        height: 50px;
        font-size: 18px;
    }
`;

const ResendRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: ${p => p.theme.colors.text.secondary};
`;

const ResendButton = styled.button`
    background: none;
    border: none;
    font-family: inherit;
    font-size: inherit;
    font-weight: 600;
    color: ${p => p.theme.colors.text.primary};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
    &:not(:disabled):hover {
        text-decoration: underline;
    }
`;

const ErrorMsg = styled.p`
    color: #ef4444;
    font-size: 13px;
    font-family: 'Inter', sans-serif;
    margin: 0 0 16px 0;
`;

const VerifyEmailScreen = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const accountType = location.state?.accountType || 'business';
    const email = location.state?.email || 'your email';

    const [digits, setDigits] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const refs = useRef([]);

    useEffect(() => {
        refs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleChange = (index, value) => {
        const char = value.replace(/\D/g, '').slice(-1);
        const next = [...digits];
        next[index] = char;
        setDigits(next);
        setError('');
        if (char && index < 5) {
            refs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            refs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pasted) return;
        const next = [...digits];
        pasted.split('').forEach((ch, i) => { if (i < 6) next[i] = ch; });
        setDigits(next);
        refs.current[Math.min(pasted.length, 5)]?.focus();
    };

    const handleVerify = () => {
        const code = digits.join('');
        if (code.length < 6) {
            setError('Please enter the complete 6-digit code.');
            return;
        }
        // Mock: any 6-digit code is accepted
        navigate(`/create/${accountType}`, { replace: true });
    };

    const handleResend = () => {
        setResendCooldown(30);
        setDigits(['', '', '', '', '', '']);
        setError('');
        refs.current[0]?.focus();
    };

    const maskedEmail = email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(b.length) + c);

    return (
        <PageContainer>
            <BackButton onClick={() => navigate(-1)}>
                <ArrowLeft size={16} />
                Back
            </BackButton>

            <Card>
                <LogoImg src={logo} alt="NYBANQ" />

                <IconCircle>
                    <Mail size={28} />
                </IconCircle>

                <Title>Check your email</Title>
                <Subtitle>
                    We sent a 6-digit verification code to{' '}
                    <EmailBadge>{maskedEmail}</EmailBadge>.{' '}
                    The code expires in 10 minutes.
                </Subtitle>

                <OtpRow onPaste={handlePaste}>
                    {digits.map((d, i) => (
                        <OtpInput
                            key={i}
                            ref={el => refs.current[i] = el}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={d}
                            $filled={!!d}
                            $error={!!error}
                            onChange={e => handleChange(i, e.target.value)}
                            onKeyDown={e => handleKeyDown(i, e)}
                        />
                    ))}
                </OtpRow>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <GradientButton
                    colors={['#333333', theme.colors.text?.dark || '#111827']}
                    onPress={handleVerify}
                    disabled={digits.join('').length < 6}
                    style={{ width: '100%', padding: '15px', borderRadius: '10px' }}
                >
                    <span style={{ fontFamily: 'Inter', fontSize: '16px', fontWeight: '600' }}>
                        Verify & Continue
                    </span>
                </GradientButton>

                <ResendRow>
                    Didn't receive it?
                    <ResendButton onClick={handleResend} disabled={resendCooldown > 0}>
                        <RefreshCw size={12} />
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </ResendButton>
                </ResendRow>
            </Card>

            <LegalFooter style={{ position: 'relative', marginTop: '40px' }}>
                <span>© 2026 NYBANQ. All rights reserved.</span>
            </LegalFooter>
        </PageContainer>
    );
};

export default VerifyEmailScreen;
